'use client';

import { IBlogConfig } from '@/api/types/blog';
import Image from '@/components/Image';
import MagicJump, { JumpTarget } from '@/components/MagicJump';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useStore } from '@/providers/app-store-provider';
import { colors } from '@/theme/zeelool.theme';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import React, { useContext, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

// 神策参数类型
interface SensorParams {
  mkt_name: string;
  pits_index: number;
  pits_jump_type: string;
  pits_jump_content: string;
}

interface BlogBannerProps {
  banner: IBlogConfig;
  sdBlogPageMktExp?: (params: SensorParams) => void;
  sdBlogPageMktClick?: (params: SensorParams) => void;
  className?: string;
}

const BlogBanner: React.FC<BlogBannerProps> = ({
  banner,
  sdBlogPageMktExp = () => {},
  sdBlogPageMktClick = () => {},
  className = '',
}) => {
  const { deviceType } = useContext(ResponsiveContext);
  const waiMode = useStore((state) => state.waiMode);
  // 使用useInView处理曝光事件
  const [bannerRef, inView] = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  const homeBanner = useMemo(() => {
    return banner.content;
  }, [banner]);
  // 计算banner跳转目标
  const bannerJumpTarget = useMemo(() => {
    try {
      return homeBanner?.jump_target || {};
    } catch (error) {
      logger.log(error, 'error-bannerJumpTarget');
      return {};
    }
  }, [homeBanner?.jump_target]);

  // 计算神策参数
  const sensorParams = useMemo((): SensorParams => {
    return {
      mkt_name: '顶部banner',
      pits_index: 0,
      pits_jump_type: `${bannerJumpTarget.jump_type || ''}`,
      pits_jump_content: `${bannerJumpTarget.Jump_target || ''}`,
    };
  }, [bannerJumpTarget]);

  // 处理曝光事件
  useEffect(() => {
    if (inView) {
      sdBlogPageMktExp(sensorParams);
    }
  }, [inView]);

  // 点击事件处理
  const handleBeforeJump = () => {
    sdBlogPageMktClick(sensorParams);
  };

  if (!homeBanner) {
    return null;
  }

  return (
    <div ref={bannerRef} className={`blog-banner w-full ${className}`}>
      <MagicJump
        prefetch={false}
        item={bannerJumpTarget as JumpTarget}
        openInNewTab
        className={`
          block w-full
          ${bannerJumpTarget.jump_type ? 'cursor-pointer' : ''}
          ${deviceType === EPlatform.WEB ? 'h-[33.33vw] flex' : ''}
        `}
        onBeforeJump={handleBeforeJump}
        aria-label={homeBanner.title}
      >
        <WapOnly>
          {/* Banner 图片 */}
          <div className="banner_img w-full h-auto">
            {homeBanner?.wap_image && (
              <Image
                src={homeBanner.wap_image}
                alt={homeBanner.title || 'Blog Banner'}
                width={400}
                height={200}
                className="block w-full h-full object-cover"
              />
            )}
          </div>

          {/* Banner 文本内容 */}
          {homeBanner.title && (
            <div className="banner_text pt-20 px-14 box-border">
              <h3 className="banner_title text-24 font-bb text-primary-black">
                {homeBanner.title}
              </h3>
            </div>
          )}

          {/* Banner 描述 */}
          {homeBanner.description && (
            <div className="banner_content mt-12 text-[13px] font-ir leading-20 px-14 text-primary-black">
              {homeBanner.description}
            </div>
          )}
        </WapOnly>
        <WebOnly>
          <div
            className={`
              banner_text
              w-[40.625vw] h-[33.33vw]
              flex flex-col justify-center
              pl-[2.5vw] pr-[11.875vw] box-border
            `}
            style={{
              backgroundColor:
                waiMode || !homeBanner.background_color ? '#fff' : homeBanner.background_color,
            }}
          >
            <h3
              className={`
                banner_title
                text-[32px] leading-[1.2] font-bb
              `}
              style={{
                color:
                  waiMode || !homeBanner.title_color ? colors.primaryBlack : homeBanner.title_color,
              }}
            >
              {homeBanner.title}
            </h3>
            <div
              className={`
                banner_content
                mt-[12px] text-14 leading-[1.5] font-ir
                line-clamp-2 overflow-hidden
              `}
              style={{
                color:
                  waiMode || !homeBanner.description_color
                    ? colors.primaryBlack
                    : homeBanner.description_color,
              }}
            >
              {homeBanner.description}
            </div>
          </div>
          <div className="banner_img w-[calc(100vw-40.625vw)] h-[33.33vw]">
            <Image
              src={homeBanner.pc_image}
              alt={homeBanner.title}
              height={333}
              width={1000}
              className="block w-full h-full object-cover"
            />
          </div>
        </WebOnly>
      </MagicJump>
    </div>
  );
};

export default BlogBanner;
